"use client";

import React, { useState, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Check, ChevronDown, Search, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchCitiesForCountry, getCachedCities, type City } from '@/utils/cityFetcher';

interface CitySelectorProps {
  value: string;
  onChange: (value: string) => void;
  countryName: string | null;
  className?: string;
  error?: boolean;
  disabled?: boolean;
}

export const CitySelector: React.FC<CitySelectorProps> = ({
  value,
  onChange,
  countryName,
  className,
  error = false,
  disabled = false,
}) => {
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('');

  // Fetch cities when country changes
  useEffect(() => {
    if (!countryName) {
      setCities([]);
      return;
    }

    // Check cache first
    const cached = getCachedCities(countryName);
    if (cached) {
      setCities(cached);
      return;
    }

    // Fetch cities from API
    setIsLoading(true);
    fetchCitiesForCountry(countryName)
      .then((fetchedCities) => {
        setCities(fetchedCities);
      })
      .catch((error) => {
        console.error('Error loading cities:', error);
        setCities([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [countryName]);

  // Filter cities based on search input
  const filteredCities = cities.filter((city) =>
    city.label.toLowerCase().includes(filter.toLowerCase())
  );

  // Find selected city
  const selectedCity = cities.find((city) => city.label === value);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const clearFilter = () => {
    setFilter('');
  };

  return (
    <div className={cn('relative', className)}>
      <Listbox value={value} onChange={onChange} disabled={disabled || !countryName}>
        {({ open }) => (
          <>
            <Listbox.Button
              className={cn(
                'relative w-full cursor-pointer rounded-md border border-gray-200 bg-[#FAFAF9] h-10 pl-3 pr-10 text-left',
                'focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2',
                'sm:text-sm',
                error && 'border-red-500',
                (disabled || !countryName) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span className="flex items-center">
                {isLoading && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-gray-400" />
                )}
                <span className="block truncate text-black">
                  {!countryName
                    ? 'Select a country first'
                    : isLoading
                    ? 'Loading cities...'
                    : selectedCity?.label || value || 'Select city'}
                </span>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown
                  className={cn(
                    'h-5 w-5 text-gray-400 transition-transform duration-200',
                    open && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={React.Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                static
                className="absolute z-10 max-h-60 min-w-[320px] w-[320px] overflow-auto rounded-md bg-white mt-2 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                {/* Search Input */}
                <div className="sticky -top-2 z-10 bg-white p-2">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Search className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="text"
                      value={filter}
                      onChange={handleFilterChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && filter.trim()) {
                          e.preventDefault();
                          e.stopPropagation();
                          onChange(filter.trim());
                          setFilter('');
                        }
                      }}
                      placeholder="Search or type city name..."
                      className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-black placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {filter && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearFilter();
                        }}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>

                {/* City List */}
                {isLoading ? (
                  <div className="relative cursor-default select-none py-3 px-4 text-gray-700 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading cities...</span>
                  </div>
                ) : filteredCities.length === 0 ? (
                  <div className="relative cursor-default select-none py-3 px-4 text-gray-700">
                    {filter ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange(filter.trim());
                          setFilter('');
                        }}
                        className="text-left w-full hover:bg-gray-100 -mx-4 px-4 py-1 rounded"
                      >
                        <div className="text-sm font-medium text-gray-900">
                          Use "{filter}"
                        </div>
                        <div className="text-xs text-gray-500">Click to select this city</div>
                      </button>
                    ) : cities.length === 0 ? (
                      'Type your city name above'
                    ) : (
                      'No matching cities found. Type your city name above.'
                    )}
                  </div>
                ) : (
                  <>
                    {filteredCities.map((city) => (
                      <Listbox.Option
                        key={city.id}
                        className={({ active, selected }) =>
                          cn(
                            'relative cursor-pointer select-none py-3 px-3',
                            active ? 'bg-gray-100' : 'text-gray-900',
                            selected && 'bg-gray-50'
                          )
                        }
                        value={city.label}
                      >
                        {({ selected }) => (
                          <div className="flex items-center gap-2">
                            <div className="flex h-4 w-4 items-center justify-center">
                              {selected ? (
                                <Check className="h-4 w-4 text-black" aria-hidden="true" />
                              ) : null}
                            </div>
                            <span className="truncate text-sm text-gray-700">{city.label}</span>
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                    {filter && !filteredCities.some(c => c.label.toLowerCase() === filter.toLowerCase()) && (
                      <div className="border-t border-gray-200 mt-2 pt-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange(filter.trim());
                            setFilter('');
                          }}
                          className="text-left w-full hover:bg-gray-100 px-3 py-2 rounded"
                        >
                          <div className="text-sm font-medium text-gray-900">
                            Use "{filter}"
                          </div>
                          <div className="text-xs text-gray-500">City not in list? Add it manually</div>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
};
